import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';

import { Customer } from '~/types/Customer';
import { apiFetch, refreshAuthToken } from '~/utils/apiFetch';

interface CustomerContextType {
  customer: Customer | null;
  loading: boolean;
  error: string | null;
  refetchCustomer: () => Promise<void>;
  clearCustomer: () => Promise<void>;
  setCustomerUrl: (_url: string) => void;
}

const CustomerContext = createContext<CustomerContextType | undefined>(undefined);

export const CustomerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const clearAuthState = useCallback(() => {
    setCustomer(null);
    if (typeof window !== 'undefined') {
      localStorage.removeItem('userUrl');
    }
  }, []);

  const fetchCustomer = useCallback(async () => {
    if (typeof window === 'undefined') {
      setLoading(false);
      return;
    }

    const userUrl = localStorage.getItem('userUrl');

    setLoading(true);
    setError(null);

    if (!userUrl) {
      // No userUrl in localStorage means user is not logged in
      setCustomer(null);
      setLoading(false);
      return;
    }

    try {
      const response = await apiFetch(`${window.ENV?.API_URL}${userUrl}`);

      if (!response.ok) {
        // If unauthorized, clear the auth state
        if (response.status === 401 || response.status === 403) {
          clearAuthState();
          throw new Error('Unauthorized');
        }
        throw new Error(`Failed to fetch customer: ${response.status}`);
      }

      const data = await response.json();
      setCustomer(data);
    } catch (err: unknown) {
      setCustomer(null);
      if (err instanceof Error) {
        setError(err.message || 'Failed to load user');
      } else {
        setError('Failed to load user');
      }
    } finally {
      setLoading(false);
    }
  }, [clearAuthState]);

  useEffect(() => {
    fetchCustomer();
  }, [fetchCustomer]);

  // The BEARER cookie lives only 15 minutes and Remix actions/loaders cannot refresh it
  // server-side (the refresh_token cookie never reaches them). Keep it fresh from the
  // browser while a customer is logged in, so a form submitted after a pause does not
  // hit the API anonymously; refresh again when a backgrounded tab comes back.
  useEffect(() => {
    if (!customer || typeof window === 'undefined') return undefined;

    const REFRESH_INTERVAL_MS = 10 * 60 * 1000;
    let lastRefresh = Date.now();

    const refresh = () => {
      lastRefresh = Date.now();
      // Failure is fine here — the reactive 401 handling in apiFetch stays the fallback.
      refreshAuthToken().catch(() => {});
    };

    const intervalId = window.setInterval(refresh, REFRESH_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible' && Date.now() - lastRefresh > REFRESH_INTERVAL_MS) {
        refresh();
      }
    };
    document.addEventListener('visibilitychange', onVisible);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [customer]);

  const clearCustomer = useCallback(async () => {
    // Call backend to clear HttpOnly JWT cookies
    try {
      await fetch(`${window.ENV?.API_URL}/api/v2/shop/customers/logout`, {
        method: 'POST',
        credentials: 'include',
      });
    } catch {
      // Ignore errors - proceed with local cleanup anyway
    }

    clearAuthState();
  }, [clearAuthState]);

  // Allow setting the userUrl from login pages
  const setCustomerUrl = useCallback((url: string) => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('userUrl', url);
    }
  }, []);

  return (
    <CustomerContext.Provider
      value={{
        customer,
        loading,
        error,
        refetchCustomer: fetchCustomer,
        clearCustomer,
        setCustomerUrl,
      }}
    >
      {children}
    </CustomerContext.Provider>
  );
};

export const useCustomer = () => {
  const context = useContext(CustomerContext);
  if (!context) {
    throw new Error('useCustomer must be used within a CustomerProvider');
  }
  return context;
};

/**
 * Safe version of useCustomer that returns null when outside CustomerProvider.
 * Use this in components that may render during SSR or in edge cases where
 * the provider might not be available (e.g., GTM tracking components).
 */
export const useCustomerSafe = (): CustomerContextType | null => {
  return useContext(CustomerContext) ?? null;
};
